import React, { useEffect, useState } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUserTie, faUser, faCheckCircle, faTimesCircle, faUsers, faFilter, 
  faBuilding, faUserCheck, faUserXmark 
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';

const EmployeesReportPage: React.FC = () => {
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterMerchant, setFilterMerchant] = useState('');
  const [merchants, setMerchants] = useState<any[]>([]);

  useEffect(() => {
    loadMerchants();
    loadEmployees();
  }, [filterMerchant]);

  const loadMerchants = async () => {
    try {
      const response = await merchantApi.get('/merchants');
      setMerchants(response.data.data || []);
    } catch (error) {
      console.error('Error loading merchants');
    }
  };

  const loadEmployees = async () => {
    try {
      let url = '/admin/employees';
      if (filterMerchant) url += `?merchant_id=${filterMerchant}`;
      
      const response = await merchantApi.get(url);
      setEmployees(response.data.data || []);
    } catch (error: any) {
      toast.error('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  };

  // Stats
  const activeCount = employees.filter(e => e.is_active).length;
  const inactiveCount = employees.filter(e => !e.is_active).length;

  // Define columns for DataTable
  const columns: Column<any>[] = [
    {
      key: 'full_name',
      header: 'Name',
      sortable: true,
      render: (emp) => (
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white
                          ${emp.role === 'manager' ? 'bg-purple-500' : 'bg-blue-500'}`}>
            <FontAwesomeIcon icon={emp.role === 'manager' ? faUserTie : faUser} />
          </div>
          <div>
            <p className="font-semibold text-gray-800">{emp.full_name}</p>
            <p className="text-xs text-gray-400">{emp.email}</p>
          </div>
        </div>
      )
    },
    {
      key: 'merchant',
      header: 'Merchant',
      render: (emp) => (
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600">
            <FontAwesomeIcon icon={faBuilding} className="text-xs" />
          </div>
          <div>
            <p className="font-medium text-gray-800 text-sm">{emp.merchants?.name || '-'}</p>
            <p className="text-xs text-gray-400">{emp.merchants?.email}</p>
          </div>
        </div>
      )
    },
    {
      key: 'role',
      header: 'Role',
      sortable: true,
      render: (emp) => (
        emp.role === 'manager' ? (
          <span className="px-2.5 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faUserTie} /> Manager
          </span>
        ) : (
          <span className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faUser} /> Employee
          </span>
        )
      )
    },
    {
      key: 'last_login_at',
      header: 'Last Login',
      sortable: true,
      render: (emp) => (
        emp.last_login_at ? (
          <div>
            <p className="text-sm text-gray-700">
              {new Date(emp.last_login_at).toLocaleDateString('th-TH')}
            </p>
            <p className="text-xs text-gray-400">
              {new Date(emp.last_login_at).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        ) : (
          <span className="text-gray-400 text-sm">ยังไม่เคย</span>
        )
      )
    },
    {
      key: 'is_active',
      header: 'Status',
      sortable: true,
      render: (emp) => (
        emp.is_active ? (
          <span className="px-2.5 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faCheckCircle} /> Active
          </span>
        ) : (
          <span className="px-2.5 py-1 bg-red-100 text-red-600 rounded-full text-xs font-semibold inline-flex items-center gap-1">
            <FontAwesomeIcon icon={faTimesCircle} /> Inactive
          </span>
        )
      )
    }
  ];

  return (
    <div className="max-w-full">
      {/* Premium Header */}
      <div className="relative overflow-hidden rounded-2xl mb-6 p-6 sm:p-8"
           style={{ background: 'var(--theme-primary-gradient)' }}>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl"
               style={{ backgroundColor: 'var(--theme-primary)', opacity: 0.3 }}></div>
          <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full blur-3xl"
               style={{ backgroundColor: 'var(--theme-secondary)', opacity: 0.3 }}></div>
          <div className="absolute inset-0 opacity-10"
               style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '24px 24px' }}></div>
        </div>

        <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg"
                   style={{ 
                     background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.05) 100%)',
                     backdropFilter: 'blur(10px)',
                     boxShadow: '0 8px 32px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.2)'
                   }}>
                <FontAwesomeIcon icon={faUsers} className="text-2xl text-white" />
              </div>
              <div className="absolute inset-0 rounded-2xl animate-ping"
                   style={{ backgroundColor: 'var(--theme-primary)', opacity: 0.3 }}></div>
            </div>
            
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Employees Report
              </h1>
              <p className="text-white/70 text-sm mt-1 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                รายงานพนักงานทั้งหมด • {employees.length} คน
              </p>
            </div>
          </div>

          {/* Filter */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-white/70 text-sm"
                 style={{ background: 'rgba(255,255,255,0.1)' }}>
              <FontAwesomeIcon icon={faFilter} />
            </div>
            <select 
              value={filterMerchant} 
              onChange={(e) => setFilterMerchant(e.target.value)}
              className="px-4 py-2.5 rounded-xl text-sm font-medium bg-white/10 text-white border border-white/20 
                         focus:outline-none focus:ring-2 focus:ring-white/30 backdrop-blur-sm"
            >
              <option value="" className="text-gray-800">All Merchants</option>
              {merchants.map((m) => (
                <option key={m.id} value={m.id} className="text-gray-800">{m.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 shadow-theme flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white"
               style={{ background: 'var(--theme-primary-gradient)' }}>
            <FontAwesomeIcon icon={faUsers} />
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase font-medium">Total Employees</p>
            <p className="text-xl font-bold" style={{ color: 'var(--theme-primary)' }}>{employees.length}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-theme flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center text-white">
            <FontAwesomeIcon icon={faUserCheck} />
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase font-medium">Active</p>
            <p className="text-xl font-bold text-green-600">{activeCount}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-theme flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center text-white">
            <FontAwesomeIcon icon={faUserXmark} />
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase font-medium">Inactive</p>
            <p className="text-xl font-bold text-amber-600">{inactiveCount}</p>
          </div>
        </div>
      </div>

      {/* Employees Table with DataTable */}
      <DataTable
        data={employees}
        columns={columns}
        loading={loading}
        emptyMessage="ไม่มีข้อมูลพนักงาน"
        emptyIcon={<FontAwesomeIcon icon={faUsers} className="text-5xl text-gray-200" />}
        pagination={{ pageSize: 20, showPageSizeSelector: true }}
        hoverable
      />
    </div>
  );
};

export default EmployeesReportPage;
